import { useState, type FormEvent } from 'react';
import { Button } from '@/components/button';
import { Dialog } from '@/components/dialog';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { useToast } from '@/components/toast';
import {
  CONTACT_ACTIVITY_KINDS,
  useCreateContactActivity,
  type ContactActivityKind,
} from '@/lib/contact-activity';
import { formatStatus } from '@/lib/format';

type Props = {
  contactId: number;
  /** Set from a lead screen so the entry records the lead it came out of. */
  leadId?: number;
  onClose: () => void;
};

export function LogActivityDialog({ contactId, leadId, onClose }: Props) {
  const create = useCreateContactActivity(contactId, leadId);
  const toast = useToast();
  const [kind, setKind] = useState<ContactActivityKind>('note');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    await create.mutateAsync({
      kind,
      title: title.trim() || defaultTitleFor(kind),
      body: body.trim() || null,
    });
    toast.success('Activity logged');
    onClose();
  }

  return (
    <Dialog open={true} onClose={onClose} title="Log activity">
      <form className="space-y-3" onSubmit={handleSubmit}>
        <label className="block space-y-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Type
          </span>
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value as ContactActivityKind)}
          >
            {CONTACT_ACTIVITY_KINDS.map((k) => (
              <option key={k} value={k}>
                {formatStatus(k)}
              </option>
            ))}
          </Select>
        </label>

        <Input
          placeholder={defaultTitleFor(kind)}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Textarea
          placeholder="Details (optional)"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />

        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Log it'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function defaultTitleFor(kind: ContactActivityKind): string {
  switch (kind) {
    case 'call':
      return 'Call';
    case 'email':
      return 'Email';
    case 'meeting':
      return 'Meeting';
    case 'sms':
      return 'Text';
    default:
      return 'Note';
  }
}
